import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import {
  CreateNotePayload,
  GetNotesParams,
  Note,
  NoteFolder,
  NoteTag,
  NoteWithTodos,
  SearchNotesParams
} from 'interfaces/note.interface';
import { HTTP_STATS_CODE, PAGE } from 'constants/general';
import { fetchToDosByIds } from '../to-do/extra';
import Endpoints from 'constants/endpoints';
import type { RootState } from 'store';
import { setHasDataLoadFailed } from '../general/slice';
import { isApiDataValid, isRecord } from 'utils/api/api-data-validity.util';
import { getNoteListScopeParams } from 'utils/note-folder.util';

export const getNotes = createAsyncThunk(
  'note/get_notes',
  async (params: GetNotesParams = {}, { dispatch, getState }) => {
    try {
      const { data } = await focusBearApi.get(Endpoints.NOTE.BASE, {
        params: {
          page: params.page ?? PAGE.FIRST,
          take: params.take ?? PAGE.TAKE,
          completed_activity_id: params.completed_activity_id,
          tag_id: params.tag_id,
          order: params.order ?? 'DESC',
          ...getNoteListScopeParams((getState() as RootState).note)
        }
      });
      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        return null;
      }
      return data;
    } catch (error) {
      captureException(error);
      return null;
    }
  }
);

/**
 * Every answer but 401 resolves here. Until backend#2663 is deployed the path is read
 * as a note id and fails (often a 500), and the page falls back to the flat list, so
 * there is nothing to log or redirect for. 401 still goes through the token refresh.
 */
const NOTE_FOLDERS_REQUEST_CONFIG = {
  validateStatus: (status: number) => status !== HTTP_STATS_CODE.UNAUTHORIZED
};

export const getNoteFolders = createAsyncThunk(
  'note/get_note_folders',
  async (): Promise<NoteFolder[] | null> => {
    try {
      const { data, status } = await focusBearApi.get<unknown>(
        Endpoints.NOTE.FOLDERS,
        NOTE_FOLDERS_REQUEST_CONFIG
      );
      return status === HTTP_STATS_CODE.SUCCESS && Array.isArray(data)
        ? (data as NoteFolder[])
        : null;
    } catch (error) {
      captureException(error);
      return null;
    }
  }
);

/** Only the folder changes; the backend doesn't count it as an edit to the note. */
export const moveNoteOutOfFolder = createAsyncThunk(
  'note/move_note_out_of_folder',
  async (noteId: string): Promise<boolean> => {
    try {
      const payload: Pick<CreateNotePayload, 'id' | 'folder_id'> = {
        id: noteId,
        folder_id: null
      };
      const { status } = await focusBearApi.put(Endpoints.NOTE.BASE, payload);
      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(t('notes_page.folders.move_out_failed'));
        return false;
      }
      toast.success(t('notes_page.folders.moved_out'));
      return true;
    } catch (error) {
      captureException(error);
      toast.error(t('notes_page.folders.move_out_failed'));
      return false;
    }
  }
);

export const getNoteById = createAsyncThunk(
  'note/get_note_by_id',
  async (noteId: string): Promise<NoteWithTodos | null> => {
    try {
      const { data } = await focusBearApi.get<Note>(
        `${Endpoints.NOTE.BASE}/${noteId}`
      );
      if (data && data.embedded_todo_ids?.length) {
        const todos = await fetchToDosByIds(data.embedded_todo_ids);
        return { ...data, embedded_todos: todos };
      }
      return data ? { ...data, embedded_todos: [] } : null;
    } catch (error) {
      captureException(error);
      return null;
    }
  }
);

export const getUserNoteTags = createAsyncThunk(
  'note/get_user_tags',
  async (): Promise<NoteTag[]> => {
    try {
      const { data } = await focusBearApi.get<NoteTag[]>(Endpoints.NOTE.TAGS);
      return data ?? [];
    } catch (error) {
      captureException(error);
      return [];
    }
  }
);

export const searchNotes = createAsyncThunk(
  'note/search_notes',
  async (params: SearchNotesParams): Promise<Note[]> => {
    try {
      const { data } = await focusBearApi.get<Note[]>(Endpoints.NOTE.SEARCH, {
        params: {
          query: params.query,
          take: params.take ?? 20
        }
      });
      return data ?? [];
    } catch (error) {
      captureException(error);
      return [];
    }
  }
);

export const createOrUpdateNote = createAsyncThunk(
  'note/create_or_update_note',
  async (
    { payload, isUpdate }: { payload: CreateNotePayload; isUpdate: boolean },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const { data, status } = await focusBearApi.put(
        Endpoints.NOTE.BASE,
        payload
      );

      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(
          t(
            isUpdate
              ? 'notes_page.could_not_update_note'
              : 'notes_page.could_not_create_note'
          )
        );
        return rejectWithValue('http_error');
      }

      const isBrainDump = payload.is_brain_dump === true;
      if (!isBrainDump) {
        const { currentPage, searchQuery } = (getState() as RootState).note;
        const take = PAGE.TAKE.NOTES;
        try {
          const refreshed = await dispatch(
            getNotes({
              page: isUpdate ? currentPage : PAGE.FIRST,
              take
            })
          ).unwrap();
          if (refreshed === null) {
            toast.error(t('notes_page.could_not_process_the_request'));
            return rejectWithValue(null);
          }
          if (searchQuery.trim()) {
            await dispatch(
              searchNotes({ query: searchQuery, take: 20 })
            ).unwrap();
          }
        } catch (refreshError) {
          captureException(refreshError);
          toast.error(t('notes_page.could_not_process_the_request'));
          return rejectWithValue(null);
        }

        toast.success(
          t(
            isUpdate
              ? 'notes_page.note_updated_successfully'
              : 'notes_page.note_created_successfully'
          )
        );
      }
      return data;
    } catch (error) {
      captureException(error);
      toast.error(t('notes_page.could_not_process_the_request'));
      return rejectWithValue('request_failed');
    }
  }
);

export const deleteNote = createAsyncThunk(
  'note/delete_note',
  async (noteId: string, { dispatch, getState }) => {
    try {
      const { status } = await focusBearApi.delete(Endpoints.NOTE.BASE, {
        params: { note_id: noteId }
      });

      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(t('notes_page.could_not_process_the_request'));
        return false;
      }

      const { currentPage, searchQuery } = (getState() as RootState).note;
      const take = PAGE.TAKE.NOTES;
      try {
        const refreshed = await dispatch(
          getNotes({ page: currentPage, take })
        ).unwrap();
        if (refreshed === null) {
          toast.error(t('notes_page.could_not_process_the_request'));
        }
        if (searchQuery.trim()) {
          await dispatch(
            searchNotes({ query: searchQuery, take: 20 })
          ).unwrap();
        }
      } catch (refreshError) {
        captureException(refreshError);
        toast.error(t('notes_page.could_not_process_the_request'));
      }

      toast.success(t('notes_page.note_deleted_successfully'));
      return true;
    } catch (error) {
      captureException(error);
      toast.error(t('notes_page.could_not_process_the_request'));
      return false;
    }
  }
);
